import { isAppMode } from "@/platform/app-mode/client"
import { screenIsVisible, subscribeToScreenVisibility } from "@/platform/app-mode/screen-visibility"
import { impressions } from "@/platform/observability/impressions"
import { createSpanTracker, recordClick } from "@/platform/observability/impressions/span-tracker"
import type { SpanTracker } from "@/platform/observability/impressions/span-tracker"

const SERVE_ATTRIBUTE = "data-nelo-serve"

const THRESHOLDS = [0, 0.5, 1]
const VISIBLE_FRACTION = 0.5

export interface ImpressionOptions {
  container?: boolean
  // For a `display: contents` group, which has no box to observe.
  region?: boolean
}

interface Target {
  container: boolean
  region: boolean
  refs: number
}

interface Registry {
  tracker: SpanTracker
  observer: IntersectionObserver
  targets: Map<Element, Map<string, Target>>
  intersectingContainers: Set<Element>
  regions: Set<Element>
  remeasure: () => void
  setElementVisible: (element: Element, serveId: string, visible: boolean) => void
  teardown: () => void
}

let registry: Registry | undefined
const handledClicks = new WeakSet<Event>()

const area = (rect: { width: number; height: number }) => Math.max(0, rect.width) * Math.max(0, rect.height)

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight })

const coversViewport = (visibleArea: number, viewport: { width: number; height: number }) => {
  const viewportArea = area(viewport)
  return viewportArea > 0 && visibleArea / viewportArea >= VISIBLE_FRACTION
}

const regionRect = (element: Element) => {
  const rects = [...element.children].map((child) => child.getBoundingClientRect()).filter((rect) => rect.width > 0 && rect.height > 0)
  if (rects.length === 0) return { top: 0, bottom: 0, left: 0, right: 0, width: 0, height: 0 }
  const top = Math.min(...rects.map((rect) => rect.top))
  const bottom = Math.max(...rects.map((rect) => rect.bottom))
  const left = Math.min(...rects.map((rect) => rect.left))
  const right = Math.max(...rects.map((rect) => rect.right))
  return { top, bottom, left, right, width: right - left, height: bottom - top }
}

const containerVisibleNow = (element: Element, region: boolean) => {
  const rect = region ? regionRect(element) : element.getBoundingClientRect()
  const viewport = viewportSize()
  const visible = {
    width: Math.min(rect.right, viewport.width) - Math.max(rect.left, 0),
    height: Math.min(rect.bottom, viewport.height) - Math.max(rect.top, 0),
  }
  const visibleArea = area(visible)
  const elementArea = area(rect)
  return (elementArea > 0 && visibleArea / elementArea >= VISIBLE_FRACTION) || coversViewport(visibleArea, viewport)
}

const entryVisible = (entry: IntersectionObserverEntry, container: boolean) => {
  if (!entry.isIntersecting) return false
  if ((entry.intersectionRatio ?? 0) >= VISIBLE_FRACTION) return true
  return container && entry.intersectionRect != null && coversViewport(area(entry.intersectionRect), entry.rootBounds ?? viewportSize())
}

const screenActive = () => document.visibilityState !== "hidden" && (!isAppMode() || screenIsVisible())

const createRegistry = (): Registry => {
  const tracker = createSpanTracker({ onImpression: (serveId, occurredAt) => impressions.impression(serveId, new Date(occurredAt)) })
  const targets = new Map<Element, Map<string, Target>>()
  const intersectingContainers = new Set<Element>()
  const regions = new Set<Element>()
  const visibleElements = new Map<string, Set<Element>>()

  // Form-factor variants render the same section twice, one of them hidden, so a serve is
  // visible while any of its elements is.
  const setElementVisible = (element: Element, serveId: string, visible: boolean) => {
    const elements = visibleElements.get(serveId) ?? new Set<Element>()
    if (visible) elements.add(element)
    else elements.delete(element)
    if (elements.size > 0) visibleElements.set(serveId, elements)
    else visibleElements.delete(serveId)
    tracker.setVisible(serveId, elements.size > 0)
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const serves = targets.get(entry.target)
        if (!serves) continue
        if (entry.isIntersecting && [...serves.values()].some((target) => target.container)) intersectingContainers.add(entry.target)
        else intersectingContainers.delete(entry.target)
        for (const [serveId, target] of serves) setElementVisible(entry.target, serveId, entryVisible(entry, target.container))
      }
    },
    { threshold: THRESHOLDS }
  )

  // The thresholds never fire again while a section taller than the viewport scrolls
  // through it, so containers are re-measured on scroll to apply the viewport rule.
  let frame: number | undefined
  const remeasureContainers = () => {
    if (frame !== undefined || (intersectingContainers.size === 0 && regions.size === 0)) return
    frame = requestAnimationFrame(() => {
      frame = undefined
      for (const element of [...intersectingContainers, ...regions]) {
        for (const [serveId, target] of targets.get(element) ?? []) {
          if (target.container) setElementVisible(element, serveId, containerVisibleNow(element, target.region))
        }
      }
    })
  }

  const syncActive = () => tracker.setActive(screenActive())
  syncActive()
  document.addEventListener("visibilitychange", syncActive)
  const unsubscribeScreen = subscribeToScreenVisibility(syncActive)
  window.addEventListener("scroll", remeasureContainers, { passive: true })
  window.addEventListener("resize", remeasureContainers, { passive: true })

  return {
    tracker,
    observer,
    targets,
    intersectingContainers,
    regions,
    remeasure: remeasureContainers,
    setElementVisible,
    teardown: () => {
      observer.disconnect()
      tracker.dispose()
      document.removeEventListener("visibilitychange", syncActive)
      unsubscribeScreen()
      window.removeEventListener("scroll", remeasureContainers)
      window.removeEventListener("resize", remeasureContainers)
      if (frame !== undefined) cancelAnimationFrame(frame)
    },
  }
}

const activeRegistry = (): Registry | undefined => {
  if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return undefined
  registry ??= createRegistry()
  return registry
}

export const observeImpression = (element: Element, serveId: string, { container = false, region = false }: ImpressionOptions = {}): (() => void) => {
  const current = activeRegistry()
  if (!current) return () => {}

  let serves = current.targets.get(element)
  if (!serves) {
    serves = new Map()
    current.targets.set(element, serves)
  }
  const existing = serves.get(serveId)
  if (existing) {
    existing.refs += 1
  } else {
    serves.set(serveId, { container: container || region, region, refs: 1 })
    if (region) {
      current.regions.add(element)
      current.remeasure()
    } else {
      // Re-observing delivers a fresh entry, so a serve added to an element already in view is measured.
      current.observer.unobserve(element)
      current.observer.observe(element)
    }
  }

  return () => {
    const target = serves.get(serveId)
    if (!target) return
    target.refs -= 1
    if (target.refs > 0) return
    serves.delete(serveId)
    current.setElementVisible(element, serveId, false)
    if (serves.size > 0) return
    current.targets.delete(element)
    current.intersectingContainers.delete(element)
    current.regions.delete(element)
    current.observer.unobserve(element)
  }
}

export const observeImpressionsWithin = (root: Element): (() => void) => {
  if (!activeRegistry()) return () => {}

  const observed = new Map<Element, { serveId: string; stop: () => void }>()
  const scan = () => {
    const present = new Map<Element, string>()
    for (const element of root.querySelectorAll(`[${SERVE_ATTRIBUTE}]`)) {
      const serveId = element.getAttribute(SERVE_ATTRIBUTE)
      if (serveId) present.set(element, serveId)
    }
    for (const [element, { serveId, stop }] of observed) {
      if (present.get(element) === serveId) continue
      stop()
      observed.delete(element)
    }
    for (const [element, serveId] of present) {
      if (!observed.has(element)) observed.set(element, { serveId, stop: observeImpression(element, serveId) })
    }
  }

  scan()
  const mutations = new MutationObserver(scan)
  mutations.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: [SERVE_ATTRIBUTE] })

  return () => {
    mutations.disconnect()
    for (const { stop } of observed.values()) stop()
    observed.clear()
  }
}

export const recordServeClick = (event: Event): void => {
  if (handledClicks.has(event)) return
  handledClicks.add(event)

  const origin = event.target instanceof Element ? event.target : null
  const target = (origin?.closest("a,button,[role=button]") ?? origin)?.closest(`[${SERVE_ATTRIBUTE}]`)
  const serveId = target?.getAttribute(SERVE_ATTRIBUTE)
  const current = activeRegistry()
  if (!serveId || !current) return

  recordClick({ click: (id, occurredAt) => impressions.click(id, new Date(occurredAt)) }, current.tracker, serveId)
}

export const __resetImpressionVisibilityForTests = (): void => {
  registry?.teardown()
  registry = undefined
}
